import { Loading3QuartersOutlined } from '@ant-design/icons';
import { Card, type CardProps, Collapse, Spin } from 'antd';
import clx from 'classnames';
import { lighten } from 'polished';
import type * as React from 'react';
import styled from 'styled-components';
import {
  ConfigurationCardParentProvider,
  useConfigurationCardFetching,
  useConfigurationCardLayout,
  useConfigurationCardRegister,
} from './ConfigurationCardContext.tsx';
import { ConfigurationCardHeader } from './ConfigurationCardHeader.tsx';
import {
  cardChromeCss,
  denseFlatChromeCss,
  denseHeaderPartsCss,
} from './configurationCardStyles.ts';

const StyledConfigurationCard = styled(Card)`
  ${cardChromeCss}

  > .ant-card-head {
    .ant-card-head-wrapper {
    padding: 24px 0;
    padding-bottom: 16px;
  }
  /* Permette al titolo di andare a capo invece di essere troncato con ellipsis */
  .ant-card-head-title {
    overflow: visible;
    white-space: normal;
    text-overflow: unset;
  }
  }

  &.full-width-body > .ant-card-body {
    padding: 0;

    /* TableData interno: rimuoviamo shadow/border-radius del wrapper così la
       tabella risulta flush con i bordi della ConfigurationCard senza creare
       un secondo filo visibile sotto la tabella. */
    .table-data-responsive-card {
      box-shadow: none;
      border-radius: 0;
    }
  }

  /* Vista densa: card flat, header più compatto, icona e titolo ridotti,
     descrizione mostrata come popover ⓘ accanto al titolo (vedi render).
     Stessa resa del ramo Collapse dense a card aperta: header su sfondo
     applicativo, filo di separazione e padding del contenuto allineati. */
  &.dense {
    ${denseFlatChromeCss}

    > .ant-card-head {
      padding-inline: 24px;
      min-height: 40px;
      background-color: ${props => props.theme.colorBgLayout};
      /* Nel ramo Collapse il filo è il border-top del content. */
      border-bottom: 1px solid ${props => props.theme.colorSplit};
      .ant-card-head-wrapper {
        padding: 6px 0;
      }
      .title {
        text-transform: uppercase;
      }
    }

    /* Stesso padding del content-box del ramo Collapse dense. */
    > .ant-card-body {
      padding: 12px 24px;
    }

    ${denseHeaderPartsCss}
  }
`;

/**
 * Wrapper del ramo collassabile: porta l'id di registrazione, perché
 * rc-collapse NON inoltra `id` alla radice (passa solo `aria-*`/`data-*`) e
 * navigazione densa e scroll-to cercano la card con `getElementById`.
 */
const CollapseCardWrapper = styled.div``;

/**
 * Ramo collassabile: Collapse antd a item singolo "vestito" da card. Il toggle
 * è quello nativo (header con `role="button"`, `tabIndex`, Enter,
 * `aria-expanded`) con la freccia di espansione di default.
 */
const StyledCollapseCard = styled(Collapse)`
  &&& {
    ${cardChromeCss}
    background-color: ${props => props.theme.colorBgContainer};
    /* Il content-box ha angoli quadrati: senza clip lo sfondo sborda dal
       raggio inferiore della card. */
    overflow: hidden;

    > .ant-collapse-item {
      border-bottom: none;

      > .ant-collapse-header {
        align-items: center;
        padding: 24px 24px 16px;
        background-color: transparent;
        user-select: none;

        &:hover {
          background-color: ${props => props.theme.colorBgTextHover};
        }

        &:focus-visible {
          outline: 2px solid ${props => props.theme.colorPrimary};
          outline-offset: -2px;
        }
      }

      /* Il bordo vive sul content (montato solo da aperta): da chiusa l'header
         non mostra fili, da aperta separa header e contenuto. */
      > .ant-collapse-content {
        background-color: transparent;
        border-top: 1px solid ${props => props.theme.colorSplit};

        > .ant-collapse-content-box {
          padding: 24px;
        }
      }
    }

    &.full-width-body
      > .ant-collapse-item
      > .ant-collapse-content
      > .ant-collapse-content-box {
      padding: 0;

      /* Vedi regola omologa nel ramo Card. */
      .table-data-responsive-card {
        box-shadow: none;
        border-radius: 0;
      }
    }

    &.dense {
      ${denseFlatChromeCss}

      > .ant-collapse-item {
        /* Card flat: senza il radius del root, l'ultimo item mostrerebbe i
           suoi angoli arrotondati nativi (visibili all'hover dell'header). */
        &,
        > .ant-collapse-header {
          border-radius: 0;
        }

        > .ant-collapse-header {
          min-height: 40px;
          padding: 6px 24px;

          /* Header in evidenza: sfondo applicativo, che si schiarisce
             all'hover e ancora di più a card aperta (regola sotto). */
          background-color: ${props => props.theme.colorBgLayout};

          &:hover {
            background-color: ${props =>
              lighten(0.018, props.theme.colorBgLayout)};
          }

          .title {
            text-transform: uppercase;
          }
        }

        &.ant-collapse-item-active > .ant-collapse-header {
          &,
          &:hover {
            background-color: ${props => props.theme.colorBgLayout};
          }
        }

        > .ant-collapse-content > .ant-collapse-content-box {
          padding: 12px 24px;
        }
      }

      ${denseHeaderPartsCss}
    }
  }
`;

type ConfigurationCardTitleProps =
  | { title: string; anchorTitle?: string }
  | { title: React.ReactNode; anchorTitle: string };

export type ConfigurationCardProps = Omit<CardProps, 'title'> &
  ConfigurationCardTitleProps & {
    icon: React.ReactNode;
    description: React.ReactNode;
    /** Azzera il padding del body della card (utile quando contiene una tabella) */
    fullWidthBody?: boolean;
    /**
     * Contenuto opzionale mostrato a destra nell'header (es. chip di stato).
     * Nel ramo collassabile è reso nello slot nativo `extra` del Collapse e
     * resta visibile anche quando la card è collassata.
     *
     * ATTENZIONE (ramo collassabile): i click sull'header — `extra` compreso —
     * fanno il toggle della card. Eventuali elementi interattivi nel `summary`
     * devono fermare la propagazione di click/tastiera con `stopPropagation()`
     * (pattern ufficiale antd per l'`extra`, come fa il trigger di
     * `InfoPopover`).
     */
    summary?: React.ReactNode;
    /**
     * Stato iniziale quando il group è `collapsible`. Default aperto.
     * @default true
     */
    defaultOpen?: boolean;
  };

/**
 * Card personalizzata per i componenti di configurazione con layout specifico,
 * con titolo, descrizione e icona - tutti obbligatori.
 *
 * Si auto-registra nel ConfigurationCardGroup se presente. Dentro un group
 * `dense` riduce le spaziature e mostra la `description` come popover ⓘ; dentro
 * un group `collapsible` la card è resa con il Collapse antd (toggle nativo su
 * tutto l'header, freccia di espansione nativa) e il `summary` va nello slot
 * nativo `extra` degli item del Collapse.
 *
 * Passare `fullWidthBody` per azzerare il padding del body, ad esempio quando
 * il contenuto è una tabella che deve risultare flush con i bordi della card.
 */
export function ConfigurationCard(props: ConfigurationCardProps) {
  const {
    title,
    icon,
    description,
    anchorTitle,
    fullWidthBody,
    summary,
    defaultOpen = true,
    className,
    children,
    ...rest
  } = props;
  const anchorId = useConfigurationCardRegister(
    anchorTitle ?? (title as string),
    {
      icon,
      // Il menu dense mostra sempre una stringa: se `title` è un ReactNode
      // (es. con Tag/contatori) usiamo l'`anchorTitle` (garantito dal tipo).
      label: typeof title === 'string' ? title : anchorTitle,
    },
  );
  const { dense, collapsible } = useConfigurationCardLayout();
  const fetching = useConfigurationCardFetching();

  // Nel ramo Collapse il `summary` NON va nell'header: passa dallo slot
  // nativo `extra` dell'item (vedi sotto).
  const header = (
    <ConfigurationCardHeader
      title={title}
      icon={icon}
      description={description}
      dense={dense}
      summary={collapsible ? undefined : summary}
    />
  );

  const body = (
    <Spin
      size="large"
      spinning={fetching}
      indicator={<Loading3QuartersOutlined spin />}
    >
      {children}
    </Spin>
  );

  const cardClassName = clx(className, {
    'full-width-body': fullWidthBody,
    dense,
  });

  if (collapsible) {
    // Qui niente spread di `rest`: sono CardProps, non compatibili col Collapse.
    return (
      <ConfigurationCardParentProvider value={anchorId}>
        <CollapseCardWrapper id={anchorId}>
          <StyledCollapseCard
            className={cardClassName}
            defaultActiveKey={defaultOpen ? [anchorId] : []}
            expandIconPosition="end"
            items={[
              {
                key: anchorId,
                label: header,
                children: body,
                // I campi form dentro la card devono montarsi (e registrarsi
                // nello schema) anche quando la card parte chiusa.
                forceRender: true,
                extra: summary,
              },
            ]}
          />
        </CollapseCardWrapper>
      </ConfigurationCardParentProvider>
    );
  }

  return (
    <ConfigurationCardParentProvider value={anchorId}>
      <StyledConfigurationCard
        bordered
        id={anchorId}
        {...rest}
        className={cardClassName}
        title={header}
      >
        {body}
      </StyledConfigurationCard>
    </ConfigurationCardParentProvider>
  );
}
